import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';

/**
 * 프로젝트 permission 키. 서버 카탈로그(`api/src/constants/projectPermissions.ts`)의 값을 그대로 쓴다.
 *
 * 이 타입은 자동완성과 오타 방지를 위한 것이고, **권한 판정의 기준은 언제나 서버**다.
 * Web의 메뉴·버튼 숨김은 안내일 뿐 보안 경계가 아니다.
 */
export type ProjectPermission =
  | 'project.enter'
  | 'project.contribute'
  | 'project.manage'
  | 'members.read'
  | 'members.manage'
  | 'roles.manage'
  | 'documents.read'
  | 'documents.write'
  | 'documents.manage'
  | 'plans.read'
  | 'plans.write'
  | 'reports.read'
  | 'reports.write'
  | 'codeReviews.read'
  | 'codeReviews.write'
  | 'changeSets.read'
  | 'changeSets.write'
  | 'postMortems.read'
  | 'postMortems.write'
  | 'coActions.read'
  | 'coActions.write'
  | 'conventions.read'
  | 'conventions.write'
  | 'skills.read'
  | 'skills.write'
  | 'skills.manage'
  | 'comments.read'
  | 'comments.write'
  | 'dashboard.read'
  | 'search.read'
  | 'repositories.read'
  | 'repositories.manage'
  | 'integrations.read'
  | 'integrations.write'
  | 'agents.read'
  | 'agents.write'
  | 'runners.read'
  | 'runners.requestPlan'
  | 'runners.write'
  | 'promptPresets.read'
  | 'promptPresets.write'
  | 'serviceTokens.read'
  | 'serviceTokens.write'
  | 'notifications.read'
  | 'notifications.write';

export type BuiltInProjectRoleKey =
  | 'PROJECT_OWNER'
  | 'PROJECT_ADMIN'
  | 'PROJECT_MEMBER'
  | 'PROJECT_VIEWER'
  | 'DOCUMENT_VIEWER'
  | 'DOCUMENT_EDITOR';

export type ProjectRoleSummary = {
  id: string;
  name: string;
  builtInKey: BuiltInProjectRoleKey | null;
};

export type ProjectAccess = {
  projectId: string;
  permissions: ProjectPermission[];
  roles: ProjectRoleSummary[];
};

export type ProjectRole = {
  id: string;
  name: string;
  description: string | null;
  builtInKey: BuiltInProjectRoleKey | null;
  isBuiltIn: boolean;
  archivedAt: string | null;
  permissions: ProjectPermission[];
  assignedMemberCount: number;
};

export type ProjectPermissionCatalogItem = {
  key: ProjectPermission;
  feature: string;
  action: string;
};

type Envelope<T> = { data: T };

const getJson = async <T>(token: string, path: string, failure: string): Promise<T> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}${path}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw await parseApiError(response, `${failure}: ${response.status}`);
  }
  return ((await response.json()) as Envelope<T>).data;
};

const sendJson = async <T>(
  token: string,
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
  failure: string,
): Promise<T> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}${path}`, {
    method,
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await parseApiError(response, `${failure}: ${response.status}`);
  }
  return ((await response.json()) as Envelope<T>).data;
};

/** 현재 사용자의 유효 permission. 멤버 전체 목록을 받아오지 않고 본인 권한만 조회한다. */
export const fetchProjectAccess = (token: string, projectId: string): Promise<ProjectAccess> =>
  getJson<ProjectAccess>(token, `/api/projects/${projectId}/access`, 'Failed to fetch project access');

export const fetchProjectRoles = (token: string, projectId: string): Promise<ProjectRole[]> =>
  getJson<ProjectRole[]>(token, `/api/projects/${projectId}/roles`, 'Failed to fetch project roles');

/** permission 목록은 화면에 하드코딩하지 않고 서버 카탈로그를 그린다. */
export const fetchProjectPermissionCatalog = async (
  token: string,
  projectId: string,
): Promise<ProjectPermissionCatalogItem[]> => {
  const data = await getJson<{ permissions: ProjectPermissionCatalogItem[] }>(
    token,
    `/api/projects/${projectId}/permission-catalog`,
    'Failed to fetch project permission catalog',
  );
  return data.permissions;
};

export const createProjectRole = (
  token: string,
  projectId: string,
  input: { name: string; description?: string | null; permissions: ProjectPermission[] },
): Promise<ProjectRole> =>
  sendJson<ProjectRole>(token, `/api/projects/${projectId}/roles`, 'POST', input, 'Failed to create project role');

export const updateProjectRole = (
  token: string,
  projectId: string,
  roleId: string,
  input: { name?: string; description?: string | null; permissions?: ProjectPermission[] },
): Promise<ProjectRole> =>
  sendJson<ProjectRole>(
    token,
    `/api/projects/${projectId}/roles/${roleId}`,
    'PUT',
    input,
    'Failed to update project role',
  );

export const setProjectRoleArchived = (
  token: string,
  projectId: string,
  roleId: string,
  archived: boolean,
): Promise<ProjectRole> =>
  sendJson<ProjectRole>(
    token,
    `/api/projects/${projectId}/roles/${roleId}/${archived ? 'archive' : 'unarchive'}`,
    'POST',
    {},
    'Failed to change project role archive state',
  );

export const setMemberProjectRoles = (
  token: string,
  projectId: string,
  memberId: string,
  roleIds: string[],
): Promise<{ memberId: string; legacyRole: string; roles: ProjectRole[] }> =>
  sendJson(
    token,
    `/api/projects/${projectId}/members/${memberId}/roles`,
    'PUT',
    { roleIds },
    'Failed to update member project roles',
  );
